import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLazyGetProfileQuery } from "../../../app/appApiSlice";
import type { NotificationItemModel } from "../types";
import {
  canAttemptGraphUserPhotoLookup,
  getGraphUserPhotoUrl,
} from "../../../app/msgraphapi/graphPhotoService";

interface NotificationItemProps {
  item: NotificationItemModel;
  isLast?: boolean;
}

const isFollowNotification = (item: NotificationItemModel) =>
  item.type?.toLowerCase() === "follow" ||
  item.message?.toLowerCase().includes("started following you") ||
  item.title?.toLowerCase().includes("started following you");

const isWelcomeMessageNotification = (item: NotificationItemModel) =>
  item.type?.toLowerCase() === "welcomemessage";

const isBroadcastNotification = (item: NotificationItemModel) =>
  item.type?.toLowerCase() === "broadcast";

// Extracts "Amit Verma" from "Amit Verma started following you"
const extractSenderName = (title: string): string | null => {
  const suffix = " started following you";
  const idx = title.toLowerCase().lastIndexOf(suffix.toLowerCase());
  return idx > 0 ? title.substring(0, idx).trim() : null;
};

const DEFAULT_NOTIFICATION_AVATAR = "/assets/img/default-user.jpg";
const WELCOME_MESSAGE_AVATAR = "/assets/img/DeloitteLogo.jpeg";
const BROADCAST_AVATAR = "/assets/img/DeloitteLogo.jpeg";
const WELCOME_MESSAGE_TEXT = "We are live and just getting started...\nMore from the new Alumni experience\nis coming your way soon!";
const CHAT_THREAD_LINK_PATTERN = /\[([^\]]+)\]\((\/conversations\?threadId=[^)]+)\)/i;

type ParsedChatThreadLink = {
  beforeText: string;
  groupName: string;
  routePath: string;
  afterText: string;
};

const parseChatThreadLink = (message?: string): ParsedChatThreadLink | null => {
  const normalizedMessage = typeof message === "string" ? message : "";
  if (!normalizedMessage) {
    return null;
  }

  const match = normalizedMessage.match(CHAT_THREAD_LINK_PATTERN);
  if (!match || match.index === undefined) {
    return null;
  }

  const [fullMatch, groupName, routePath] = match;
  if (!groupName || !routePath) {
    return null;
  }

  return {
    beforeText: normalizedMessage.slice(0, match.index),
    groupName,
    routePath,
    afterText: normalizedMessage.slice(match.index + fullMatch.length),
  };
};

const isValidThreadRoute = (routePath?: string): boolean => {
  if (!routePath || !routePath.includes("threadId=")) {
    return false;
  }
  // Extract threadId and validate it's not empty
  const threadIdMatch = routePath.match(/threadId=([^&]+)/);
  if (!threadIdMatch || !threadIdMatch[1]) {
    return false;
  }
  return true;
};

const isDefaultOrEmpty = (url?: string) => {
  const normalized = typeof url === "string" ? url.trim().toLowerCase() : "";
  return (
    !normalized ||
    normalized.endsWith("/default-user.jpg") ||
    normalized.endsWith("default-user.jpg")
  );
};

const NotificationItem = ({ item, isLast }: NotificationItemProps) => {
  const navigate = useNavigate();
  const [fetchProfile] = useLazyGetProfileQuery();
  const [resolvedAvatar, setResolvedAvatar] = useState("");

  useEffect(() => {
    // Step 1: use photoUrl from API if valid
    const apiAvatar = item.imageUrl?.trim() ?? "";

    if (apiAvatar && !isDefaultOrEmpty(apiAvatar)) {
      setResolvedAvatar(apiAvatar);
      return;
    }

    if (!item.entityId) {
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        // Step 2: fetch profile by entityId (resourceId) to get profilePic + primaryEmail
        const profileResult = await fetchProfile(item.entityId!, true).unwrap();
        const profilePic = profileResult?.data?.profilePic?.trim() ?? "";
        const primaryEmail = profileResult?.data?.primaryEmail?.trim() ?? "";

        // Step 3: use blob/CDN profile photo if available
        if (!cancelled && profilePic && !isDefaultOrEmpty(profilePic)) {
          setResolvedAvatar(profilePic);
          return;
        }

        // Step 4: fallback to Graph API using primaryEmail (same as ChatMessage)
        if (!cancelled && primaryEmail && canAttemptGraphUserPhotoLookup(primaryEmail)) {
          const photoUrl = await getGraphUserPhotoUrl(primaryEmail);
          if (!cancelled && photoUrl) {
            setResolvedAvatar(photoUrl);
          } else if (!cancelled) {
          }
        } else if (!cancelled) {
        }
      } catch (err) {
        // silently fall back to default avatar
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [item.imageUrl, item.entityId, fetchProfile]);

  const isWelcomeMessage = isWelcomeMessageNotification(item);
  const isBroadcast = isBroadcastNotification(item);
  const finalAvatar = isWelcomeMessage
    ? WELCOME_MESSAGE_AVATAR
    : isBroadcast
    ? BROADCAST_AVATAR
    : resolvedAvatar || DEFAULT_NOTIFICATION_AVATAR;
  const parsedChatThreadLink = parseChatThreadLink(item.message);

  const isFollow = isFollowNotification(item);
  const senderName = isFollow ? extractSenderName(item.title) : null;
  const profilePath = item.entityId ? `/alumini-directory/${item.entityId}` : null;
  const profileState = item.entityId ? { passedId: item.entityId } : undefined;

  const handleRowClick = () => {
    if (profilePath) {
      navigate(profilePath, { state: profileState });
    }
  };

  const showFollowBack = isFollow && !!item.entityId;

  return (
    <div
      className={`${!isLast ? "border-b border-b-[#EFEFEF]" : ""} px-[15px] py-5 ${isFollow && profilePath ? "cursor-pointer hover:bg-gray-50 transition-colors" : ""}`}
      onClick={isFollow && profilePath ? handleRowClick : undefined}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          {/* Avatar — links to profile for follow notifications */}
          {profilePath ? (
            <Link
              to={profilePath}
              state={profileState}
              onClick={(e) => e.stopPropagation()}
              className="shrink-0"
            >
              <img
                src={finalAvatar}
                alt="Notification"
                className="h-10 w-10 object-cover hover:opacity-80 transition-opacity"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = DEFAULT_NOTIFICATION_AVATAR;
                }}
              />
            </Link>
          ) : (
            <img
              src={finalAvatar}
              alt="Notification"
              className="h-10 w-10 shrink-0 object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src = DEFAULT_NOTIFICATION_AVATAR;
              }}
            />
          )}

          <div className="min-w-0">
            <p className={`text-sm text-black leading-5 ${isWelcomeMessage || isBroadcast ? "" : "line-clamp-2"}`}>
              {isBroadcast ? (
                <span className="font-normal whitespace-pre-wrap">
                  <span className="font-semibold mr-1">Deloitte Messages </span>
                  <span>{item.message ?? item.title}</span>
                </span>
              ) : senderName ? (
                <>
                  {profilePath ? (
                    <Link
                      to={profilePath}
                      state={profileState}
                      onClick={(e) => e.stopPropagation()}
                      className={`font-semibold text-[#0070D2]${showFollowBack ? " underline underline-offset-2 decoration-[#0070D2] decoration-solid" : " hover:underline"}`}
                    >
                      {senderName}
                    </Link>
                  ) : (
                    <span className={`font-semibold text-[#0070D2]${showFollowBack ? " underline underline-offset-2 decoration-[#0070D2] decoration-solid" : ""}`}>{senderName}</span>
                  )}
                  <span className="font-semibold">{item.title.substring(senderName.length)}</span>
                </>
              ) : (
                <span className={`font-semibold${showFollowBack ? " underline underline-offset-2 decoration-solid" : ""}`}>{isWelcomeMessage ? "" : item.title}</span>
              )}
              {isWelcomeMessage ? (
                <span className="block font-normal whitespace-pre-line">{WELCOME_MESSAGE_TEXT}</span>
              ) : parsedChatThreadLink && isValidThreadRoute(parsedChatThreadLink.routePath) ? (
                <span className="font-normal">
                  {` ${parsedChatThreadLink.beforeText}`}
                  <Link
                    to={parsedChatThreadLink.routePath}
                    onClick={(e) => e.stopPropagation()}
                    className="font-semibold text-[#020304] underline underline-offset-2 decoration-[#020304] decoration-solid"
                    title="Click to view group chat"
                  >
                    {parsedChatThreadLink.groupName}
                  </Link>
                  {parsedChatThreadLink.afterText}
                </span>
              ) : (
                // For broadcast notifications we already render the full message above,
                // so avoid rendering `item.message` again here to prevent duplication.
                !isBroadcast && item.message && <span className="font-normal"> {item.message}</span>
              )}
            </p>
            <p className="text-xs text-[#99A1AF] mt-0.5">{item.timeAgo}</p>

            {/* {showFollowBack && (
              <button
                onClick={handleFollowBack}
                disabled={followedBack || isLoading}
                className={`mt-2 flex items-center gap-1 rounded px-3 py-1 text-xs font-semibold text-white transition-colors
                  ${followedBack
                    ? "cursor-default bg-gray-400"
                    : "bg-green-600 hover:bg-green-700 disabled:opacity-60"
                  }`}
              >
                {followedBack ? (
                  <><TiTick className="text-sm" /> Following</>
                ) : (
                  isLoading ? "Following..." : "Follow Back"
                )}
              </button>
            )} */}
          </div>
        </div>

        {!item.isRead && (
          <span
            className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary"
            aria-label="Unread notification"
            title="Unread"
          />
        )}
      </div>
    </div>
  );
};

export default NotificationItem;
